import { ReactNode } from 'react';
import { FiCheck } from 'react-icons/fi';

/**
 * Selo de "escolhido". O contorno rosa do design dá 2,66:1 sobre branco, abaixo dos 3:1 que
 * um indicador de estado precisa (WCAG 1.4.11): o visto azul-marinho sobre o rosa (5,34:1) é
 * o indicador que conta, e por isso acompanha toda opção escolhida.
 */
export function SelectedBadge() {
  return (
    <span
      aria-hidden="true"
      data-testid="selected-badge"
      className="absolute -right-[11px] -top-[11px] flex h-[26px] w-[26px] items-center justify-center rounded-full bg-brand-pink text-brand-navy shadow-[0_2px_8px_rgba(0,45,75,0.22)]"
    >
      <FiCheck size={14} strokeWidth={3.2} />
    </span>
  );
}

/** Moldura dos cartões com ilustração (comportamentos e tipos); a disposição de dentro é de cada etapa. */
export const ILLUSTRATED_TILE_CLASSES = 'items-center rounded-jj-lg border-[3px]';

interface ChoiceRadioProps {
  /** Nome do grupo de rádio: as opções do mesmo grupo compartilham o valor. */
  name: string;
  value: string;
  checked: boolean;
  onSelect: () => void;
  disabled?: boolean;
  /** Nome acessível, quando ele precisa dizer mais que o texto visível (o motivo de um bloqueio). */
  ariaLabel?: string;
}

/**
 * O rádio nativo de uma opção, fora da vista: quem aparece é o irmão dele dentro do `label`, que
 * reage ao foco e ao bloqueio pelo `peer`.
 *
 * `scroll-mb`: o navegador rola até o elemento com foco, que aqui é este ponto de 1px no topo do
 * cartão. A margem de rolagem tem a altura do maior cartão, para o cartão inteiro vir à vista.
 */
export function ChoiceRadio({ name, value, checked, onSelect, disabled = false, ariaLabel }: ChoiceRadioProps) {
  return (
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      disabled={disabled}
      onChange={onSelect}
      aria-label={ariaLabel}
      className="peer sr-only scroll-mb-32"
    />
  );
}

interface ChoiceTileProps extends ChoiceRadioProps {
  /** Classes da superfície: espessura da borda, raio, alinhamento e recuo de cada tipo de cartão. */
  className?: string;
  /** Legenda sob o cartão, dentro do mesmo controle (o "ENVIAR OPÇÃO N" das mensagens). */
  caption?: ReactNode;
  children: ReactNode;
}

/**
 * Opção de escolha única em forma de cartão. É um rádio nativo dentro do `label`: as setas
 * andam pelo grupo e o leitor de tela anuncia "N de M" sem nenhum `aria` montado à mão.
 */
export function ChoiceTile({
  name,
  value,
  checked,
  onSelect,
  disabled = false,
  ariaLabel,
  className = '',
  caption,
  children,
}: ChoiceTileProps) {
  return (
    <label className={`relative flex flex-col ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <ChoiceRadio name={name} value={value} checked={checked} onSelect={onSelect} disabled={disabled} ariaLabel={ariaLabel} />

      <span
        className={`flex flex-1 bg-brand-white transition-[transform,box-shadow,border-color] duration-jj-base ease-jj-standard peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-blue-dark peer-disabled:opacity-50 peer-disabled:grayscale motion-reduce:transform-none motion-reduce:transition-none ${
          checked
            ? '-translate-y-1 border-brand-pink shadow-[0_0_0_5px_rgba(254,110,145,0.28),0_14px_26px_rgba(0,45,75,0.20)]'
            : 'border-surface-200 shadow-jj-01'
        } ${className}`}
      >
        {children}
      </span>

      {caption}

      {checked && <SelectedBadge />}
    </label>
  );
}
